import { tutorialTaskDone } from '../../../../shared/src/tutorial/events'
import { useVoiceInput, VOICE_PROBLEM_TEXT } from '../../../../shared/src/redesign/data/useVoiceInput'
import apiClient from '@/api/client'
import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Send, Bot } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { toast } from '@/components/ui/Toast'
import { EmptyState } from '@/components/ui'
import { useAuthStore } from '@/store/authStore'
import { chatApi } from '@/api/chatApi'
import type { ChatMessage } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'

export const AIAssistantPage: React.FC = () => {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId)
  const [message, setMessage] = useState('')
  const [voiceBusy, setVoiceBusy] = useState(false)
  const [voiceError, setVoiceError] = useState('')
  const recorder = useVoiceInput(async (audio) => {
    if (!currentAssistantId) return
    setVoiceBusy(true)
    setVoiceError('')
    try {
      const form = new FormData()
      const ext = audio.type.includes('mp4') ? 'mp4' : audio.type.includes('ogg') ? 'ogg' : 'webm'
      form.append('audio', audio, `voice.${ext}`)
      const { data } = await apiClient.post<{ text: string }>(`/assistants/${currentAssistantId}/transcribe`, form)
      if (!data.text?.trim()) throw new Error('empty')
      setMessage(previous => [previous.trim(), data.text.trim()].filter(Boolean).join(' '))
    } catch { setVoiceError('Не удалось распознать запись. Попробуйте ещё раз или напишите текстом.') }
    finally { setVoiceBusy(false) }
  })
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: history = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['chat-history', currentAssistantId],
    queryFn: () => chatApi.getHistory(currentAssistantId!),
    enabled: !!currentAssistantId,
    refetchInterval: query => query.state.data?.some(m => m.status === 'pending' || m.status === 'processing') ? 1500 : 10000,
  })

  const sendMutation = useMutation({
    mutationFn: (content: string) => chatApi.enqueueMessage(currentAssistantId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history'] })
      setMessage('')
    },
    onError: () => toast.error('Сообщение не отправлено. Текст сохранён, попробуйте ещё раз'),
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [history])

  useEffect(() => {
    if (history.some(m => m.role === 'assistant' && m.status === 'completed' && m.content?.trim())) tutorialTaskDone('hub')
  }, [history])

  const handleSend = () => {
    if (!message.trim() || !currentAssistantId || sendMutation.isPending || voiceBusy || recorder.phase !== 'idle') return
    sendMutation.mutate(message.trim())
  }

  if (!currentAssistantId) {
    return <><Header title="Штаб" subtitle="Помощник по работе с клубом" /><EmptyState icon={Bot} title="Выберите направление" /></>
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 65px)' }}>
      <Header title="Штаб" subtitle="Помощник по работе с клубом" />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {isError && <p role="alert" className="pc-error">Не удалось загрузить разговор. <button className="underline" onClick={() => void refetch()}>Повторить</button></p>}
          {isLoading ? <p role="status" className="pc-muted">Загружаем разговор...</p> : history.length === 0 && !isError ? (
            <div className="text-center py-16">
              <Bot size={48} style={{ color: 'var(--text-hint)', margin: '0 auto' }} />
              <h3 className="text-[16px] font-semibold mt-4" style={{ color: 'var(--text-primary)' }}>
                AI Ассистент
              </h3>
              <p className="text-[14px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                Задайте вопрос о ваших спортсменах, тренировках или получите рекомендации
              </p>
            </div>
          ) : (
            history.map((msg: ChatMessage) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[70%] rounded-2xl px-4 py-3"
                  style={{
                    backgroundColor: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: msg.role === 'user' ? 'var(--accent-on)' : 'var(--text-primary)',
                  }}
                >
                  <div className="text-[14px] whitespace-pre-wrap">{msg.content || (msg.status === 'failed' ? 'Не удалось подготовить ответ' : 'Штаб готовит ответ...')}</div>
                  <div
                    className="text-[11px] mt-1"
                    style={{ opacity: 0.6 }}
                  >
                    {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: ru })}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="px-4 py-2" data-tutorial="voice">
        {recorder.phase === 'idle' && <button className="pc-input" disabled={voiceBusy || sendMutation.isPending} onClick={() => void recorder.start()}>Записать голос</button>}
        {recorder.phase === 'starting' && <span role="status">Жду разрешения на микрофон</span>}
        {recorder.phase === 'recording' && <><span role="status">Запись: {recorder.seconds} с</span> <button className="pc-input" onClick={recorder.stop}>Готово</button></>}
        {recorder.phase !== 'idle' && <button className="pc-input" onClick={recorder.cancel}>Отменить запись</button>}
        {voiceBusy && <p role="status">Распознаю запись...</p>}
        {(voiceError || recorder.problem) && <p role="alert">{voiceError || (recorder.problem && VOICE_PROBLEM_TEXT[recorder.problem])}</p>}
      </div>
      {/* Input */}
      <div className="p-4" style={{ borderTop: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-3xl mx-auto flex gap-3">
          <input
            type="text"
            aria-label="Сообщение Штабу"
            disabled={sendMutation.isPending || voiceBusy || recorder.phase !== 'idle'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Спросите AI ассистента..."
            className="flex-1 h-11 px-4 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <button
            onClick={handleSend}
            aria-label="Отправить сообщение"
            disabled={!message.trim() || sendMutation.isPending || voiceBusy || recorder.phase !== 'idle'}
            className="w-11 h-11 rounded-xl flex items-center justify-center transition-opacity"
            style={{
              backgroundColor: 'var(--accent)',
              color: 'var(--accent-on)',
              opacity: !message.trim() ? 0.5 : 1,
            }}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
